import type { IBrand, IMarket } from 'data/schema';
import type {
  IDealerChooseResolution,
  IDealerIdOption,
  IDealerResolutionInput,
  IDealerSelectionError,
  IUnavailableDealerId,
  TDealerChoice,
  TDealerResolution,
  TDealerSelectionValidation,
  TDealerWindowInput,
  TDealerWindowResolution,
} from './dealerResolution.types';
import { validateDealerId } from './dealerIdValidator';
import type { IDealerIdValidationContext } from './dealerIdValidator';
import { parseDealerId } from './dealerIdFormat';
import { getSingleFittingDealerId, getUnavailableReason, orderDealerIdOptions } from './dealerIdOptions';

/** Thin adapter over validateDealerId (AVS-4406): format, then ownership, then brand/market, each with a distinct reason. */
export function validateDealerSelection(
  dealerId: string,
  { dealerIds, brand, market }: IDealerIdValidationContext
): TDealerSelectionValidation {
  const validation = validateDealerId(dealerId, { dealerIds, brand, market });

  if (validation.ok) {
    return { ok: true };
  }

  return { ok: false, error: { reason: validation.error.reason, dealerId: validation.error.dealerId } };
}

/** The rejection for a Dealer ID ruled out this visit (with the reason its failure gave), else `undefined`. */
function findRuledOut(
  dealerId: string,
  unavailable: readonly IUnavailableDealerId[]
): IDealerSelectionError | undefined {
  const entry = unavailable.find((candidate) => candidate.dealerId === dealerId);

  return entry ? { reason: getUnavailableReason(entry.code), dealerId } : undefined;
}

/**
 * Why a URL, stored or session Dealer ID cannot be used: ruled out this visit first, then the
 * ownership and brand/market validation. `null` when it can be used.
 */
function findRejection(
  dealerId: string,
  context: IDealerIdValidationContext,
  unavailable: readonly IUnavailableDealerId[]
): IDealerSelectionError | null {
  const ruledOut = findRuledOut(dealerId, unavailable);
  if (ruledOut) {
    return ruledOut;
  }

  const validation = validateDealerSelection(dealerId, context);

  return validation.ok ? null : validation.error;
}

/** Owned, format-valid and not ruled out this visit, in any brand or market. */
function isAvailablePreviousChoice(
  dealerId: string | undefined,
  dealerIds: readonly string[],
  unavailable: readonly IUnavailableDealerId[]
): dealerId is string {
  return (
    dealerId !== undefined &&
    dealerIds.includes(dealerId) &&
    !!parseDealerId(dealerId) &&
    !findRuledOut(dealerId, unavailable)
  );
}

interface IGetPreselectedDealerIdContext {
  options: readonly IDealerIdOption[];
  linkedDealerId?: string;
  storedDealerId?: string;
  lastConfirmedDealerId?: string;
  dealerIds: readonly string[];
  unavailable: readonly IUnavailableDealerId[];
}

/**
 * The window's pre-selection: the fitting, available link ID, else the previous choice — the stored
 * choice even of another brand/market, else the last confirmed Dealer ID — when it is still owned,
 * format-valid and not ruled out, else nothing.
 */
function getPreselectedDealerId({
  options,
  linkedDealerId,
  storedDealerId,
  lastConfirmedDealerId,
  dealerIds,
  unavailable,
}: IGetPreselectedDealerIdContext): string | undefined {
  if (linkedDealerId && options.some((option) => option.dealerId === linkedDealerId && option.isFromLink)) {
    return linkedDealerId;
  }

  if (isAvailablePreviousChoice(storedDealerId, dealerIds, unavailable)) {
    return storedDealerId;
  }

  if (isAvailablePreviousChoice(lastConfirmedDealerId, dealerIds, unavailable)) {
    return lastConfirmedDealerId;
  }

  return undefined;
}

/**
 * Builds the window: every Dealer ID as an option, the link ID first when it fits, the remembered
 * reason, and the resolver's own pre-selection. When every Dealer ID is ruled out it still returns
 * `choose`: there is no `blocked` status.
 */
function buildChoose(
  {
    dealerIds,
    brand,
    market,
    storedDealerId,
    unavailable = [],
    lastConfirmedDealerId,
  }: TDealerWindowInput & { storedDealerId?: string },
  error?: IDealerSelectionError,
  linkedDealerId?: string
): IDealerChooseResolution {
  const options = orderDealerIdOptions(dealerIds, { brand, market }, { unavailable, linkedDealerId });
  const preselectedDealerId = getPreselectedDealerId({
    options,
    linkedDealerId,
    storedDealerId,
    lastConfirmedDealerId,
    dealerIds,
    unavailable,
  });

  return {
    status: 'choose',
    options,
    ...(error ? { error } : {}),
    ...(preselectedDealerId ? { preselectedDealerId } : {}),
  };
}

/**
 * The account's only Dealer ID when it fits the brand and market and is not ruled out, unless a
 * different link ID is given; else `undefined`.
 */
function findAutoDealerId(
  { dealerIds, brand, market, unavailable = [] }: TDealerWindowInput,
  urlDealerId?: string
): string | undefined {
  const singleFittingDealerId = getSingleFittingDealerId(dealerIds, { brand, market });

  return singleFittingDealerId &&
    !findRuledOut(singleFittingDealerId, unavailable) &&
    (!urlDealerId || urlDealerId === singleFittingDealerId)
    ? singleFittingDealerId
    : undefined;
}

/**
 * Re-resolves the window when no link, stored or session Dealer ID takes part: a fitting single
 * Dealer ID is picked automatically, otherwise every Dealer ID is offered. Never returns `none`.
 * An empty `dealerIds` is defined as `choose` with no options; it is unreachable because callers
 * hold a committed Dealer ID. Gives the same result as `resolveDealerForLogin` for a non-empty list
 * without a link, stored or session Dealer ID.
 */
export function resolveDealerWindow(input: TDealerWindowInput): TDealerWindowResolution {
  const autoDealerId = findAutoDealerId(input);

  return autoDealerId ? { status: 'selected', dealerId: autoDealerId, source: 'auto' } : buildChoose(input);
}

/**
 * Decides which dealer applies after login.
 * Order: (1) a fitting single-Dealer-ID account auto-starts, unless a different link ID is given;
 * (2) a URL dealer ID, valid for the URL and not ruled out, is offered first in the window — a
 * rejected one remembers its reason and the stored choice/hint are not consulted; (3) else the
 * stored selection, if valid and not ruled out; a rejected one remembers its reason; (4) else the
 * session's own dealer ID, a hint silently skipped when invalid or ruled out; (5) otherwise every
 * Dealer ID is offered, with the remembered reason and the resolver's own pre-selection. When every
 * Dealer ID is ruled out, step 5 still returns `choose`: there is no `blocked` status.
 */
export function resolveDealerForLogin(input: IDealerResolutionInput): TDealerResolution {
  const { dealerIds, brand, market, urlDealerId, storedDealerId, sessionDealerId, unavailable = [] } = input;
  const context = { dealerIds, brand, market };

  if (dealerIds.length === 0) {
    return { status: 'none' };
  }

  const autoDealerId = findAutoDealerId(input, urlDealerId);
  if (autoDealerId) {
    return { status: 'selected', dealerId: autoDealerId, source: 'auto' };
  }

  if (urlDealerId) {
    const rejection = findRejection(urlDealerId, context, unavailable);

    return rejection ? buildChoose(input, rejection) : buildChoose(input, undefined, urlDealerId);
  }

  if (storedDealerId) {
    const rejection = findRejection(storedDealerId, context, unavailable);

    return rejection
      ? buildChoose(input, rejection)
      : { status: 'selected', dealerId: storedDealerId, source: 'stored' };
  }

  if (sessionDealerId && !findRejection(sessionDealerId, context, unavailable)) {
    return { status: 'selected', dealerId: sessionDealerId, source: 'session' };
  }

  return buildChoose(input);
}

interface IResolveDealerChoiceContext {
  dealerIds: readonly string[];
  brand: IBrand;
  market: IMarket;
  unavailable?: readonly IUnavailableDealerId[];
}

/**
 * Decides what pressing OK on a Dealer ID in the window means: stay, switch, or reject.
 * An ID ruled out this visit is rejected with its unavailable reason. Otherwise it is validated
 * against its own brand/market (from parseDealerId); an ID that does not parse is validated against
 * the URL's brand/market instead, which gives invalid-format. An accepted ID returns its own brand
 * and market and whether that differs from the URL's (a context switch).
 */
export function resolveDealerChoice(
  dealerId: string,
  { dealerIds, brand, market, unavailable }: IResolveDealerChoiceContext
): TDealerChoice {
  const ruledOut = findRuledOut(dealerId, unavailable ?? []);

  if (ruledOut) {
    return { status: 'rejected', error: ruledOut };
  }

  const parsed = parseDealerId(dealerId);
  const ownBrand = parsed?.brand ?? brand;
  const ownMarket = parsed?.market ?? market;

  const validation = validateDealerSelection(dealerId, { dealerIds, brand: ownBrand, market: ownMarket });

  if (!validation.ok) {
    return { status: 'rejected', error: validation.error };
  }

  return {
    status: 'accepted',
    brand: ownBrand,
    market: ownMarket,
    switchesContext: ownBrand !== brand || ownMarket !== market,
  };
}
